import React, { useMemo, useState } from 'react';
import {
	DocumentationFile,
	useLazyDocumentationFilesQuery,
} from '../../../../graphql/graphql';
import Collapse from '../../../../Components/HealthPlan/Collapse/Collapse';
import { ECardView } from '../../Enums/ECardView.enum';
import ECardGrid from '../ECardGrid/ECardGrid';
import ECardList from '../ECardList/ECardList';
import Loading from '../../../../Components/Loading';
import { useEffectAsync } from '../../../../helpers';
import Text from '../../../../Components/Base/Text/Text';
import { FileToShare } from '../ECard/ECard';
import { DocumentationFolderData } from '../ECardWrapper/ECardWrapper';

// @ts-ignore
import styles from './ECardFolder.module.scss';
import { useAppContext } from '../../../../Services/AppContext';
import { ECardType } from '../../Enums/ECardType.enum';

interface Props {
	item: DocumentationFolderData;
	view: ECardView;
	patientId: string;
	filter?: string;
	isShareMode: boolean;
	onItemCheck: (file: FileToShare, isChecked: boolean) => void;
	filesToShare: FileToShare[];
	updateFolder: (folderId: string) => void;
	eCardType: ECardType;
}

const ECardFolder: React.FC<Props> = ({
	item,
	view,
	patientId,
	filter,
	isShareMode,
	onItemCheck,
	filesToShare,
	updateFolder,
	eCardType,
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [files, setFiles] = useState<DocumentationFile[]>([]);
	const [getFiles] = useLazyDocumentationFilesQuery();
	const { socket } = useAppContext();

	const title = useMemo(() => {
		if (!!filter) {
			return `${item.name} (${files?.length ? files.length : 0})`;
		}
		if (item && item?.filesCount && item.filesCount > 0) {
			return `${item.name} (${item.filesCount})`;
		}
		return `${item.name} (0)`;
	}, [item, files, filter]);

	const handleLoadFiles = async (folderId?: string) => {
		setIsOpen(true);
		setIsLoading(true);
		//RELOAD FOLDERS AFTER MOVE TO DIFFERENT ONE
		if (folderId && folderId !== item.id) {
			updateFolder(folderId);
			updateFolder(item.id);
		}
		const data = await getFiles({
			userId: patientId,
			folderId: item.id,
			fulltext: filter,
		}).unwrap();
		setFiles(data?.documentationFiles?.data as DocumentationFile[]);
		setIsLoading(false);
	};

	const handleCloseCollapse = () => {
		setIsOpen(false);
	};

	const reloadData = async (folderId: string, needReload: boolean) => {
		if (needReload) {
			await handleLoadFiles(folderId);
		}
	};

	useEffectAsync(async () => {
		if (item.doSync && isOpen) {
			await handleLoadFiles();
		}
	}, [item]);

	useEffectAsync(async () => {
		if (!!filter) {
			await handleLoadFiles();
		}
	}, [filter]);

	const updateFile = (fileId: string, newData: any) => {
		const tempData = files ? [...files] : undefined;
		if (tempData) {
			for (const tempItemKey in tempData) {
				const tempItem = tempData[tempItemKey];
				if (tempItem.id === fileId) {
					tempData[tempItemKey] = { ...tempItem, ...newData };
				}
			}
			setFiles(tempData);
		}
	};

	socket.on('message', data => {
		if (typeof data.generatedNewThumb !== 'undefined') {
			files?.map(file => {
				if (file.id === data.generatedNewThumb) {
					updateFile(file.id, data.updatedData);
				}
			});
		} else if (typeof data.avResult !== 'undefined') {
			files?.map(file => {
				if (file.id === data.avResult) {
					updateFile(file.id, data.updatedData);
				}
			});
		}
	});

	let component: React.ReactElement = <></>;

	switch (view) {
		case ECardView.GRID:
			component = (
				<ECardGrid
					items={files}
					reloadData={reloadData}
					isShareMode={isShareMode}
					onItemCheck={onItemCheck}
					filesToShare={filesToShare}
					eCardType={eCardType}
				/>
			);
			break;
		case ECardView.LIST:
			component = (
				<ECardList
					items={files}
					reloadData={reloadData}
					isShareMode={isShareMode}
					onItemCheck={onItemCheck}
					filesToShare={filesToShare}
					eCardType={eCardType}
				/>
			);
			break;
	}

	if (files?.length < 1) {
		component = <Text tx='base.emptyFolder' className={styles.emptyTitle} />;
	}

	return (
		<Collapse
			title={title}
			defaultOpen={!!filter}
			icon={'/dist/icons/folder-solid.svg'}
			onOpen={handleLoadFiles}
			onClose={handleCloseCollapse}
			contentClassName={styles.collapse}>
			{isLoading ? <Loading /> : component}
		</Collapse>
	);
};

export default ECardFolder;
